import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { EnvironmentService } from './environment.service';
import { AuthInterceptor } from './auth.interceptor';

// Import the generated protobuf types and gRPC clients
import { ArchitecturalPlanWriteServiceClient } from '../../generated.commonjs/ApiServiceClientPb';
import { CopyArchitecturalPlanRequest, CopyArchitecturalPlanResponse } from '../../generated.commonjs/api_pb';

export interface ProjectCopyOptions {
  sourceProjectId: string;
  targetProjectId: string;
  copySharingSettings: boolean;
}

export interface ProjectCopyResult {
  success: boolean;
  sourceProjectId: string;
  targetProjectId: string;
  targetProjectPath?: string;
  filesCopied?: number;
  bytesCopied?: number;
  message?: string;
  error?: string;
}

/** Duplicates an existing project and its files to a new project, optionally copying sharing settings. */
@Injectable({
  providedIn: 'root',
})
export class ProjectCopyService {
  private environmentService = inject(EnvironmentService);
  private authInterceptor = inject(AuthInterceptor);

  private client: ArchitecturalPlanWriteServiceClient;

  constructor() {
    const apiServer = this.environmentService.getApiServer();
    const options = {
      unaryInterceptors: [this.authInterceptor],
    };
    this.client = new ArchitecturalPlanWriteServiceClient(`https://${apiServer}:443`, null, options);
  }

  /**
   * Copy a project with the specified options
   */
  copyProject(options: ProjectCopyOptions): Observable<ProjectCopyResult> {
    ClientLoggingService.log(
      'INFO',
      `[ProjectCopyService] Starting project copy from ${options.sourceProjectId} to ${options.targetProjectId}`
    );

    // Create the gRPC request
    const request = new CopyArchitecturalPlanRequest();
    request.setSourceProjectId(options.sourceProjectId);
    request.setTargetProjectId(options.targetProjectId);
    request.setCopySharingSettings(options.copySharingSettings);

    // Make the gRPC call (authentication is handled by the interceptor)
    const grpcCall = this.client.copyArchitecturalPlan(request, null);

    return from(grpcCall).pipe(
      map((response: CopyArchitecturalPlanResponse) => {
        ClientLoggingService.log(
          'INFO',
          `[ProjectCopyService] Project copy completed successfully:`,
          response.toObject()
        );

        return {
          success: response.getSuccess(),
          sourceProjectId: response.getSourceProjectId(),
          targetProjectId: response.getTargetProjectId(),
          targetProjectPath: response.getTargetProjectPath(),
          filesCopied: response.getFilesCopied(),
          bytesCopied: response.getBytesCopied(),
          message: response.getMessage(),
        };
      }),
      catchError(error => {
        ClientLoggingService.log('ERROR', '[ProjectCopyService] Error copying project:', error);

        let errorMessage = 'Failed to copy project';

        // Extract meaningful error message from gRPC error
        if (error?.message) {
          errorMessage = error.message;
        } else if (error?.details) {
          errorMessage = error.details;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }

        return throwError(() => ({
          success: false,
          sourceProjectId: options.sourceProjectId,
          targetProjectId: options.targetProjectId,
          error: errorMessage,
        }));
      })
    );
  }

  /**
   * Validate project ID format
   */
  validateProjectId(projectId: string): { valid: boolean; error?: string } {
    if (!projectId || projectId.trim().length === 0) {
      return { valid: false, error: 'Project ID is required' };
    }

    const trimmedId = projectId.trim();

    if (trimmedId.length < 3) {
      return { valid: false, error: 'Project ID must be at least 3 characters long' };
    }

    if (trimmedId.length > 50) {
      return { valid: false, error: 'Project ID must be no more than 50 characters long' };
    }

    // Allow alphanumeric characters, dots, underscores, and hyphens
    const validPattern = /^[a-zA-Z0-9._-]+$/;
    if (!validPattern.test(trimmedId)) {
      return { valid: false, error: 'Project ID can only contain letters, numbers, dots, underscores, and hyphens' };
    }

    return { valid: true };
  }

  /**
   * Generate a suggested target project ID based on source project ID
   */
  generateSuggestedTargetId(sourceProjectId: string): string {
    const baseId = `${sourceProjectId}-copy`;

    // If the suggested ID is too long, truncate the source part
    if (baseId.length > 50) {
      const maxSourceLength = 50 - '-copy'.length;
      return `${sourceProjectId.substring(0, maxSourceLength)}-copy`;
    }

    return baseId;
  }
}
